import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { useFdeRole } from "hooks/useFdeRole";
import {
  composeModule4Deck,
  loadDeckOverride,
  module4DeckIdForTci,
  saveDeckOverride,
} from "services/trainingDeckService";
import {
  isModule4CompleteForTci,
  markModule4CompleteForTci,
} from "services/fde/roleOnboardingService";

/**
 * Module 4 editor (admin) + read-only viewer with soft completion (FDE-C).
 */
const TrainingDeckOverridePage = () => {
  const { tciId } = useParams();
  const { userData, organizationId, isGlobalAdmin } = useAuth();
  const { isFdeC, fdeCTciIds } = useFdeRole();
  const institutionId =
    userData?.activeInstitutionId || userData?.institutionId || organizationId || null;
  const canEdit = isGlobalAdmin || userData?.role === "admin";
  const canViewAsFdeC = isFdeC && (!tciId || fdeCTciIds.includes(tciId));
  const canAccess = canEdit || canViewAsFdeC;

  const [loading, setLoading] = useState(true);
  const [deckMeta, setDeckMeta] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);

  const module4Done = useMemo(
    () => isModule4CompleteForTci(userData, tciId),
    [userData, tciId],
  );

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      introBody: "",
      mustDoBody: "",
      mustNotBody: "",
    },
  });

  const load = useCallback(async () => {
    if (!tciId || !institutionId) return;
    setLoading(true);
    setErrorMessage("");
    try {
      const deckId = module4DeckIdForTci(tciId);
      const composed = await composeModule4Deck(tciId, { institutionId });
      const override = await loadDeckOverride(3, institutionId, deckId);
      setDeckMeta({ deckId, composed, override });
      const slides = override?.slides?.length ? override.slides : composed.slides;
      reset({
        introBody: slides.find((s) => s.slideId === "m4-intro")?.body || "",
        mustDoBody: slides.find((s) => s.slideId === "m4-must-do")?.body || "",
        mustNotBody: slides.find((s) => s.slideId === "m4-must-not")?.body || "",
      });
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load training deck.");
    } finally {
      setLoading(false);
    }
  }, [tciId, institutionId, reset]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (values) => {
    if (!canEdit || !institutionId || !deckMeta?.deckId) return;
    setSaving(true);
    setErrorMessage("");
    try {
      const existingSlides =
        deckMeta.override?.slides?.length
          ? deckMeta.override.slides
          : deckMeta.composed?.slides || [];
      const annotateSlide = existingSlides.find((s) => s.slideId === "m4-annotate");
      await saveDeckOverride({
        level: 3,
        scopeId: institutionId,
        deckId: deckMeta.deckId,
        institutionId,
        slides: [
          {
            slideId: "m4-intro",
            order: 1,
            title: `Module 4 — ${tciId}`,
            body: values.introBody,
            dependsOn: [],
          },
          {
            slideId: "m4-must-do",
            order: 2,
            title: "Must-do behaviors",
            body: values.mustDoBody,
            dependsOn: [],
          },
          {
            slideId: "m4-must-not",
            order: 3,
            title: "Must-not-do behaviors",
            body: values.mustNotBody,
            dependsOn: [],
          },
          ...(annotateSlide
            ? [{ ...annotateSlide, order: 4 }]
            : [
                {
                  slideId: "m4-annotate",
                  order: 4,
                  title: "How to annotate",
                  body: "Mark severity honestly and request candidate review when patterns repeat.",
                  dependsOn: [],
                },
              ]),
        ],
      });
      setSuccessMessage("Training deck override saved.");
      await load();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to save override.");
    } finally {
      setSaving(false);
    }
  };

  const onMarkComplete = async () => {
    setMarkingComplete(true);
    setErrorMessage("");
    try {
      await markModule4CompleteForTci(tciId);
      setSuccessMessage("Module 4 marked complete for this TCI. Annotation is still available either way.");
    } catch (err) {
      setErrorMessage(err?.message || "Failed to mark Module 4 complete.");
    } finally {
      setMarkingComplete(false);
    }
  };

  if (loading) return <Loading page />;

  if (!canAccess) {
    return (
      <Alert severity="warning">
        Module 4 is available to institution admins and Clinical Annotators assigned to this TCI.
      </Alert>
    );
  }

  const slides =
    deckMeta?.override?.slides?.length > 0
      ? deckMeta.override.slides
      : deckMeta?.composed?.slides || [];

  return (
    <Box>
      <DashboardPageHeader
        title={`Module 4 — ${tciId}`}
        subtitle={
          canEdit
            ? "Institution-specific Clinical Annotator curriculum for this TCI."
            : "Review this TCI curriculum before annotating live sessions (soft readiness)."
        }
      />
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}
      {deckMeta?.composed?.isTemplate && canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Using the built-in Module 4 template. Save an override to customize for your
          institution.
        </Alert>
      )}

      {!canEdit && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          {slides
            .slice()
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((slide) => (
              <Paper key={slide.slideId} sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {slide.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" whiteSpace="pre-wrap">
                  {slide.body}
                </Typography>
              </Paper>
            ))}
          {!module4Done ? (
            <LoadingButton
              variant="contained"
              loading={markingComplete}
              onClick={onMarkComplete}
            >
              Mark Module 4 complete
            </LoadingButton>
          ) : (
            <Alert severity="success">Module 4 complete for this TCI.</Alert>
          )}
        </Stack>
      )}

      {canEdit && (
        <Paper sx={{ p: 2 }}>
          <Stack component="form" spacing={2} onSubmit={handleSubmit(onSave)}>
            <Controller
              name="introBody"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Introduction" multiline minRows={3} fullWidth />
              )}
            />
            <Controller
              name="mustDoBody"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Must-do content" multiline minRows={3} fullWidth />
              )}
            />
            <Controller
              name="mustNotBody"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Must-not-do content"
                  multiline
                  minRows={3}
                  fullWidth
                />
              )}
            />
            <LoadingButton type="submit" variant="contained" loading={saving}>
              Save override
            </LoadingButton>
          </Stack>
        </Paper>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        Deck id: {deckMeta?.deckId}
      </Typography>
    </Box>
  );
};

export default TrainingDeckOverridePage;
